import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Tabs from "@/components/Tabs";
import { useQuery, useMutation } from "@apollo/client";
import { STUDENTS_DETAIL_QUERY } from "@/pages/education/universities/[universityId]/students/queries";
import { handleImageUpload } from "@/lib/uploadImage";
import { UPDATE_STUDENT_PROFILE_PHOTO_MUTATION } from "@/pages/education/universities/[universityId]/students/[studentId]/mutations";
import { PERSON_DETAILS_HEADER_QUERY } from "@/features/person/queries";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import { useState } from "react";
import Banner from "@/components/Banner";
import { LinkProps } from "@generouted/react-router/client";
import type { Params, Path } from "@/router";
import { ObjectDetailPageSkeleton } from "@/components/Skeleton";
import usePageTitle from "@/hooks/usePageTitle";
import { getPageTitle } from "../_utils";
import { PersonObjectHeading } from "@/features/person/components/PersonObjectHeading";
import { useUniversityStaffPermissions } from "@/hooks/useUniversityStaffPermissions";
import Button from "@/components/Button";
import { UploadOptions } from "@/lib/uploadImage";
import StudentNotFoundErrorBoundary from "../_components/StudentNotFoundErrorBoundary";
import AppLayout from "@/layouts/AppLayout";

interface StudentDetailLayoutProps {
  universityName?: string;
}

export default function StudentDetailLayout({ universityName: propUniversityName }: StudentDetailLayoutProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { universityId, studentId } = useParams();
  const [searchParams] = useSearchParams();
  const { shouldShowPersonProfileActions } = useUniversityStaffPermissions();
  const [error, setError] = useState<string | null>(null);

  const returnUrl = searchParams.get("returnUrl");
  const backToLinkOrDefault = returnUrl
    ? decodeURIComponent(returnUrl)
    : `/education/universities/${universityId}/students${location.search}`;

  const [backLink] = useState<LinkProps<Path, Params>>({
    to: backToLinkOrDefault as any,
    title: "Back"
  } as unknown as LinkProps<Path, Params>);

  const [updateProfilePhoto] = useMutation(UPDATE_STUDENT_PROFILE_PHOTO_MUTATION);

  const { data, loading, refetch } = useQuery(STUDENTS_DETAIL_QUERY, {
    variables: {
      input: {
        universityLegacyIds: [universityId?.toString() || ""],
        alumni: false,
        search: studentId
      }
    },
    skip: !universityId || !studentId
  });

  const student = data?.universityStudents?.nodes[0];

  const title = getPageTitle(
    loading,
    propUniversityName && student?.displayName ? `${propUniversityName} | ${student.displayName}` : undefined
  );

  usePageTitle(location.pathname, title);

  if (loading) {
    return (
      <FocusedPageLayout backLink={backLink}>
        <ObjectDetailPageSkeleton />
      </FocusedPageLayout>
    );
  }

  if (!student) {
    return (
      <AppLayout navLinks={[]} interior bottomBar>
        <StudentNotFoundErrorBoundary universityId={universityId} />
      </AppLayout>
    );
  }

  const items = [
    {
      label: "Program Progress",
      to: `/education/universities/${universityId}/students/${studentId}` as const
    },
    {
      label: "Contact",
      to: `/education/universities/${universityId}/students/${studentId}/contact` as const
    },
    {
      label: "Internships",
      to: `/education/universities/${universityId}/students/${studentId}/internships` as const
    },
    {
      label: "Test Results",
      to: `/education/universities/${universityId}/students/${studentId}/test-results` as const
    },
    {
      label: "PAT Results",
      to: `/education/universities/${universityId}/students/${studentId}/pat-results` as const
    },
    {
      label: "Notes",
      to: `/education/universities/${universityId}/students/${studentId}/notes` as const
    }
  ];

  const updateProfilePhotoWithUrl = async (imageUrl: string) => {
    try {
      const { data: updateData } = await updateProfilePhoto({
        variables: {
          personId: student.id,
          profilePhotoUrl: imageUrl,
          universityLegacyId: universityId
        },
        refetchQueries: [
          {
            query: PERSON_DETAILS_HEADER_QUERY,
            variables: { pgaId: student.pgaId }
          }
        ],
        awaitRefetchQueries: true
      });
      if (updateData?.updateProfilePhoto?.success) {
        await refetch();
        setError(null);
      } else {
        setError("Error updating profile photo");
      }
    } catch (error) {
      console.error("Error updating profile photo:", error);
      setError("Error updating profile photo");
    }
  };

  const handleImageEditClick = async () => {
    if (!studentId || !student) {
      setError("Student ID not found");
      return;
    }

    const uploadOptions: UploadOptions = {
      cloudName: import.meta.env.VITE_CLOUDINARY_CLOUDNAME,
      uploadPreset: import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET,
      cropping: true,
      sources: ["local", "google_drive"],
      multiple: false,
      croppingAspectRatio: 1,
      resourceType: "image",
      showSkipCropButton: false,
      folder: "m360-profile-photos",
      showPoweredBy: false,
      clientAllowedFormats: ["png", "jpeg", "jpg", "webp"]
    };

    try {
      await handleImageUpload(
        uploadOptions,
        async (fieldName: string, imageUrl: string) => {
          if (fieldName === "profilePhoto" && imageUrl) {
            await updateProfilePhotoWithUrl(imageUrl);
          }
        },
        () => {},
        "profilePhoto"
      );
    } catch {
      setError("Error uploading image");
    }
  };

  return (
    <FocusedPageLayout
      backLink={backLink}
      stickyActions={true}
      actions={
        shouldShowPersonProfileActions ? (
          <Button
            variant="outlined"
            icon="open_in_new"
            onClick={() => window.open(`/people/${student.pgaId}/person/overview`, "_blank")}
            testId="view-full-profile-button"
          >
            View Full Profile
          </Button>
        ) : undefined
      }
    >
      <div className="[&_table]:isolate" data-testid="student-detail-layout">
        <PersonObjectHeading
          pgaId={student.pgaId}
          hideActions={true}
          disableScrollAdjustment={true}
          onImageEditClick={handleImageEditClick}
        />

        {error && (
          <div className="mt-6 mb-4">
            <Banner
              variant="error"
              description={error}
              inline={true}
              closable={true}
              onClose={() => setError(null)}
              data-testid="banner"
            />
          </div>
        )}

        <Tabs
          tabItems={items.map(({ label, to }) => ({
            label,
            onClick: () => navigate(to as unknown as Path),
            active: (location.pathname.endsWith("/") ? location.pathname.slice(0, -1) : location.pathname) === to
          }))}
        />

        {student && <Outlet context={{ student }} />}
      </div>
    </FocusedPageLayout>
  );
}
